<template>
  <dialog
    class="SupporterModalView"
    data-theme="light"
    aria-label="Support Ball.app"
    open
  >
    <div
      class="SupporterModalView__backdrop"
      data-testid="backdrop"
      @click="closeActiveModal()"
    />
    <div
      class="SupporterModalView__windowContainer"
    >
      <section
        class="SupporterModalView__window"
      >
        <header class="SupporterModalView__header">
          <h1 class="SupporterModalView__title">
            Support Ball.app
          </h1>
          <p
            class="SupporterModalView__body"
          >
            Ball.app is 100% bootstrapped and funded by its supporters.
          </p>
        </header>

        <div class="SupporterModalView__optionButtonList">
          <SupporterButton
            v-for="option in options"
            :key="option.type"
            v-bind="option"
            :disabled="isFetching"
            @click="handleOptionButtonClick(option)"
          />
        </div>

        <footer class="SupporterModalView__footer">
          <p
            class="SupporterModalView__body"
          >
            Got connections or have an idea?
          </p>
          <FormButton
            :disabled="isFetching"
            @click="handleContactButtonClick"
          >
            Get in touch
          </FormButton>
        </footer>
      </section>
    </div>
  </dialog>
</template>

<script lang="ts" setup>
import FormButton from "@/components/FormButton.vue";
import SupporterButton from "@/components/SupporterButton.vue";
import useMembershipStore from "@/stores/useMembershipStore";
import useModalStore from "@/stores/useModalStore";
import { SupporterType } from "@shared/lib/database/enum.types";
import { ref } from "vue";

const {
  fetchCheckoutUrlForSupporterType,
} = useMembershipStore();

const {
  closeActiveModal,
} = useModalStore();

interface SupporterOption {
  type: SupporterType,
  title: string,
  body: string,
  price: number,
}

const isFetching = ref(false);

const options = ref<Array<SupporterOption>>([
  {
    type: "Supporter",
    title: "Supporter",
    body: "Help us keep the lights on",
    price: 5_00,
  },
  {
    type: "Believer",
    title: "Believer",
    body: "You believe in what we’re building",
    price: 10_00,
  },
  {
    type: "Backer",
    title: "Backer",
    body: "Back the future of local hoops",
    price: 20_00,
  },
  {
    type: "OfficialSponsor",
    title: "Official Sponsor",
    body: "Ball.app is life",
    price: 50_00,
  },
]);

async function handleOptionButtonClick (option: SupporterOption) {
  isFetching.value = true;

  const {
    url,
    error,
  } = await fetchCheckoutUrlForSupporterType(option.type);

  if (url) {
    window.location.href = url;
  } else if (error) {
    alert(error.message);
    isFetching.value = false;
  }
}

function handleContactButtonClick () {
  window.open("mailto:jonnie@ball.app", "_blank");
}
</script>

<style lang="scss" scoped>
.SupporterModalView {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  isolation: isolate;

  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  padding: 24px 16px;

  transition: none 350ms;
}

.SupporterModalView__backdrop {
  position: absolute;
  inset: 0;
  z-index: -1;

  background-color: var(--themeColorBackdrop);
  backdrop-filter: blur(6px);

  cursor: pointer;

  transition: opacity 350ms var(--easeOutBack);

  .v-leave-active & {
    transition: 350ms;
    transition-timing-function: initial;
  }

  .v-enter-from &,
  .v-leave-to & {
    opacity: 0;
  }
}

.SupporterModalView__windowContainer {
  width: 100%;
  pointer-events: none;
}

.SupporterModalView__window {
  display: grid;
  max-width: 400px;
  margin: 0 auto;

  border-radius: 16px;
  background-color: var(--themeColorNeutral0);

  text-align: center;

  pointer-events: auto;

  .v-enter-active & {
    transition: 335ms cubic-bezier(0.25, 1, 0.30, 1);
    transition-property: opacity, transform;
  }

  .v-leave-active & {
    transition: 350ms;
    transition-property: opacity;
  }

  .v-enter-from & {
    transform: scale(0.8);
    opacity: 0;
  }

  .v-leave-to & {
    opacity: 0;
  }
}

.SupporterModalView__header {
  display: grid;
  gap: 8px;
  padding: 24px 0;
}

.SupporterModalView__title {
  font: var(--fontWeightMedium) 18px / 1.5 var(--fontFamilyNormal);
  font-stretch: var(--fontWidthExtended);
}

.SupporterModalView__body {
  font: var(--fontWeightNormal) 16px / 1.5 var(--fontFamilyNormal);
  font-stretch: var(--fontWidthExtended);
  text-wrap: balance;
}

.SupporterModalView__optionButtonList {
  display: grid;
  gap: 8px;
  padding: 0 16px;
}

.SupporterModalView__footer {
  display: grid;
  gap: 16px;
  margin-top: 16px;
  padding: 16px;

  border-top: solid 1px var(--themeColorNeutral100);
}
</style>
